export const PUBLIC_LOCALES = ['br', 'en', 'es'] as const

export type PublicLocale = (typeof PUBLIC_LOCALES)[number]

const DEFAULT_PUBLIC_LOCALE: PublicLocale = 'br'

export function isPublicLocale(value: string | null | undefined): value is PublicLocale {
  return value === 'br' || value === 'en' || value === 'es'
}

export function resolvePublicLocale(value: string | null | undefined): PublicLocale {
  const normalized = (value ?? '').toLowerCase()

  if (normalized.startsWith('en')) {
    return 'en'
  }

  if (normalized.startsWith('es')) {
    return 'es'
  }

  if (normalized.startsWith('pt') || normalized.startsWith('br')) {
    return 'br'
  }

  return DEFAULT_PUBLIC_LOCALE
}

export function getLocaleFromPathname(pathname: string): PublicLocale | null {
  const firstSegment = pathname.split('/').filter(Boolean)[0]
  return isPublicLocale(firstSegment) ? firstSegment : null
}

export function stripLocalePrefix(pathname: string): string {
  const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`
  const segments = normalizedPath.split('/').filter(Boolean)

  if (segments.length === 0) {
    return '/'
  }

  if (!isPublicLocale(segments[0])) {
    return normalizedPath
  }

  const remaining = `/${segments.slice(1).join('/')}`
  return remaining === '/' || remaining === '' ? '/' : remaining
}

export function localeToI18nCode(locale: PublicLocale): string {
  return locale === 'br' ? 'pt' : locale
}

export function getI18nLanguageFromPathname(pathname: string | null | undefined): string | undefined {
  const locale = pathname ? getLocaleFromPathname(pathname) : null
  return locale ? localeToI18nCode(locale) : undefined
}

export function getLocalizedPublicPath(
  currentPathname: string,
  targetPathname: string,
  localeSource: string | null | undefined,
): string {
  const currentLocale = getLocaleFromPathname(currentPathname) ?? resolvePublicLocale(localeSource)
  const normalizedTarget = targetPathname.startsWith('/') ? targetPathname : `/${targetPathname}`

  if (normalizedTarget === '/') {
    return `/${currentLocale}`
  }

  return `/${currentLocale}${normalizedTarget}`
}
